// Chat service for handling API communication with the backend

import type { ComparisonResponse } from '../types/chat';
import { sessionManager } from '../utils/sessionUtils';

// API types
export interface ApiChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatResponse {
  question: string;
  answer: string;
  options?: string[];
  documents: Array<{
    content: string;
    metadata: Record<string, unknown>;
  }>;
  top_product?: Record<string, unknown>;
  you_might_also_like: Record<string, unknown>[];
}

export interface HealthResponse {
  status: string;
  timestamp: string;
  ragInitialized: boolean;
  leadsApi: {
    configured: boolean;
    healthy: boolean;
  };
}

class ChatService {
  private baseUrl = "/api";

  async checkHealth(): Promise<HealthResponse> {
    const response = await fetch(`${this.baseUrl}/health`);
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }
    return response.json();
  }

  async sendMessage(
    message: string,
    chatHistory: ApiChatMessage[] = [],
    model?: string,
    topProduct?: Record<string, unknown> | null,
    alternatives?: Record<string, unknown>[]
  ): Promise<ChatResponse> {
    const sessionId = sessionManager.getSessionId();
    
    const response = await fetch(`${this.baseUrl}/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message,
        chatHistory,
        model,
        sessionId,
        topProduct,
        alternatives,
      }),
    });

    if (!response.ok) {
      throw new Error(`Chat request failed: ${response.status}`);
    }

    return response.json();
  }

  async compareProducts(models: string[]): Promise<ComparisonResponse> {
    try {
      const sessionId = sessionManager.getSessionId();
      
      const response = await fetch(`${this.baseUrl}/comparison`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ models, sessionId }),
      });

      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
          if (errorData.message) {
            errorMessage += ` - ${errorData.message}`;
          }
        } catch (jsonError) {
          // If we can't parse the error response, use the default message
          console.warn('Could not parse error response:', jsonError);
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Basic validation of response structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from server');
      }
      
      return data;
    } catch (error) {
      // Re-throw with more context if it's a network error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to comparison service');
      }
      
      // Re-throw other errors as-is
      throw error;
    }
  }
}

export const chatService = new ChatService();
